'use client'

import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuShortcut,
    ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { useNodeItemContext } from '@/features/drive/entities/node/node-item-context'
import { IChildrenProps } from '@/utils/typings/children-props'
import { Fragment, ReactNode } from 'react'
import { run } from 'shared'
import {
    INodeAction,
    NodeAction,
} from '@/features/drive/functionalities/actions/node/core/shared/utils/node-action-helpers'
import { useNodeActionHelpers } from '@/features/drive/functionalities/actions/node/core/shared/utils/use-node-action-helpers'
import { NodeMoveAction } from '@/features/drive/functionalities/actions/node/core/node-move-action/node-move-action'
import { useDirectorySelectionContext } from '@/features/drive/functionalities/selection/directory-selection-context'

interface INodeItemContextMenuProps extends IChildrenProps {
    onTriggerClick?: (e: React.MouseEvent) => void
    triggerClassName?: string
    actionNodes: ReactNode | undefined
    actions: Partial<Record<NodeAction, INodeAction>>
}

export const NodeItemContextMenu = ({
    children,
    triggerClassName,
    onTriggerClick,
    actionNodes,
    actions,
}: INodeItemContextMenuProps) => {
    const { node } = useNodeItemContext()
    const {
        state: { selectedCount },
    } = useDirectorySelectionContext()

    const { getShouldShowAction } = useNodeActionHelpers()

    if (!node) return null

    return (
        <Fragment>
            {actionNodes}

            {/* Modal false is needed as there will be issues with modals opened from inside the context menu https://github.com/shadcn-ui/ui/issues/1859#issuecomment-2423190031 */}
            <ContextMenu modal={false}>
                <ContextMenuTrigger className={triggerClassName} onClick={onTriggerClick}>
                    {children}
                </ContextMenuTrigger>

                <ContextMenuContent className="w-64 bg-white" onClick={(e) => e.stopPropagation()}>
                    {selectedCount > 1 && <ContextMenuItem disabled>{selectedCount} items selected</ContextMenuItem>}

                    {run(() => {
                        return Object.entries(actions).map(([key, action]) => {
                            if (!getShouldShowAction(action)) return null

                            switch (key) {
                                case NodeAction.MOVE: {
                                    return <NodeMoveAction key={key} as="context-menu" />
                                }

                                default: {
                                    return (
                                        <ContextMenuItem key={key} onClick={action.call}>
                                            {action.label}
                                            <ContextMenuShortcut>
                                                <action.icon className="stroke-foreground h-3.5 w-3.5" />
                                            </ContextMenuShortcut>
                                        </ContextMenuItem>
                                    )
                                }
                            }
                        })
                    })}
                </ContextMenuContent>
            </ContextMenu>
        </Fragment>
    )
}
